import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  ArrowLeft, Save, Send, Plus, Trash2, ListPlus, Truck, RefreshCw, Lock, Info,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const todayStr = () => new Date().toISOString().slice(0, 10);
const computeHours = (start, stop) => {
  if (!start || !stop) return "";
  const s = new Date(start), e = new Date(stop);
  if (isNaN(s) || isNaN(e) || e <= s) return "";
  return ((e - s) / 3600000).toFixed(2);
};

// ISO datetime-local helpers ("YYYY-MM-DDTHH:MM")
const splitIso = (iso) => {
  if (!iso) return { date: "", time24: "" };
  const [d, t = ""] = iso.split("T");
  return { date: d || "", time24: t.slice(0, 5) };
};
const joinIso = (date, time24) => (date && time24 ? `${date}T${time24}` : "");
const time24to12 = (t) => {
  if (!t) return { h: 9, m: 15, ap: "AM" };
  const [H, M] = t.split(":").map(n => parseInt(n, 10) || 0);
  const ap = H >= 12 ? "PM" : "AM";
  const h = (H % 12) || 12;
  return { h, m: M, ap };
};
const time12to24 = (h, m, ap) => {
  let H = (parseInt(h, 10) || 12) % 12;
  if (ap === "PM") H += 12;
  return `${String(H).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
};
// Default plant start/stop for a given report date
const defaultStart = (d) => (d ? `${d}T09:15` : "");
const defaultStop = (d) => (d ? `${d}T17:30` : "");

function DateTimeAmPm({ value, onChange, testId }) {
  const { date, time24 } = splitIso(value);
  const { h, m, ap } = time24to12(time24);
  const update = (next) => {
    const merged = { date, time24, h, m, ap, ...next };
    const newTime = time12to24(merged.h, merged.m, merged.ap);
    onChange(joinIso(merged.date, newTime));
  };
  return (
    <div className="flex gap-1 items-center">
      <input
        type="date"
        value={date}
        onChange={(e) => update({ date: e.target.value })}
        className="border rounded px-2 py-1 text-sm flex-1 min-w-0"
        data-testid={`${testId}-date`}
      />
      <select
        value={h}
        onChange={(e) => update({ h: parseInt(e.target.value, 10) })}
        className="border rounded px-1 py-1 text-sm"
        data-testid={`${testId}-hour`}
      >
        {Array.from({ length: 12 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <span>:</span>
      <select
        value={m}
        onChange={(e) => update({ m: parseInt(e.target.value, 10) })}
        className="border rounded px-1 py-1 text-sm"
        data-testid={`${testId}-minute`}
      >
        {Array.from({ length: 60 }, (_, i) => i).map(n => <option key={n} value={n}>{String(n).padStart(2, "0")}</option>)}
      </select>
      <select
        value={ap}
        onChange={(e) => update({ ap: e.target.value })}
        className="border rounded px-1 py-1 text-sm"
        data-testid={`${testId}-ampm`}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

export default function ProductionReportForm({ user, onLogout, mode }) {
  const navigate = useNavigate();
  const params = useParams();
  const reportId = params.reportId;
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [existingStatus, setExistingStatus] = useState(null);

  // Form state
  const [reportDate, setReportDate] = useState(todayStr());
  const [plantStart, setPlantStart] = useState(defaultStart(todayStr()));
  const [plantStop, setPlantStop] = useState(defaultStop(todayStr()));
  const [workingHoursInput, setWorkingHoursInput] = useState(""); // user-editable, defaults from plant start/stop
  const [manpower, setManpower] = useState("");
  const [gasPretKg, setGasPretKg] = useState("");
  const [selectedPos, setSelectedPos] = useState([]); // [{id, po_number, customer_name, ...}]
  const [items, setItems] = useState([]); // production items
  const [batchId, setBatchId] = useState("");
  const [poConsumption, setPoConsumption] = useState({}); // {po_id: kg}
  const [totalSqFt, setTotalSqFt] = useState("");
  const [reworkSqFt, setReworkSqFt] = useState("");
  const [sqftLocked, setSqftLocked] = useState(false);
  const [sqftInfo, setSqftInfo] = useState(null); // { unmatched_count, unmatched_codes }
  const [sqftComputing, setSqftComputing] = useState(false);
  const [sqftAreaMap, setSqftAreaMap] = useState({}); // product_code -> area_per_piece (ft²) | null
  const [notes, setNotes] = useState("");

  // Data
  const [batches, setBatches] = useState([]);
  const [shades, setShades] = useState([]);
  const [makes, setMakes] = useState([]);

  // Popups
  const [poPickerOpen, setPoPickerOpen] = useState(false);
  const [openPos, setOpenPos] = useState([]);
  const [poPickerSelected, setPoPickerSelected] = useState({}); // {id: true}
  const [poSearch, setPoSearch] = useState("");

  // Feature D P2 — auto-prefill from pallet scans
  const [source, setSource] = useState("manual"); // "manual" | "pallet_auto"
  const [linkedPalletIds, setLinkedPalletIds] = useState([]);
  const [autoPrefilling, setAutoPrefilling] = useState(false);

  // Pallet picker dialog state
  const [palletPickerOpen, setPalletPickerOpen] = useState(false);
  const [candidatePallets, setCandidatePallets] = useState([]);
  const [pickedPalletIds, setPickedPalletIds] = useState({}); // {pallet_id: true}
  const [palletPickerLoading, setPalletPickerLoading] = useState(false);
  const [palletFilterFrom, setPalletFilterFrom] = useState(todayStr());
  const [palletFilterTo, setPalletFilterTo] = useState(todayStr());
  const [palletSearch, setPalletSearch] = useState("");

  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [productPickerSelected, setProductPickerSelected] = useState({}); // {key: true} where key = po_id|product_code

  // Add batch inline
  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [newBatchForm, setNewBatchForm] = useState({ batch_no: "", shade_id: "", make_id: "" });
  const [newShadeInline, setNewShadeInline] = useState("");
  const [newMakeInline, setNewMakeInline] = useState("");

  const computedHours = useMemo(() => computeHours(plantStart, plantStop), [plantStart, plantStop]);
  const workingHoursEdited = workingHoursInput !== "";
  const workingHours = workingHoursEdited ? workingHoursInput : computedHours;
  // Auto-sync working hours from plant start/stop unless user has edited the field
  useEffect(() => {
    if (!workingHoursEdited) setWorkingHoursInput("");
    // eslint-disable-next-line
  }, [plantStart, plantStop]);
  // Withdrawn kg auto-totals per-PO consumption entries
  const withdrawnKg = useMemo(() => {
    const total = Object.values(poConsumption).reduce((sum, v) => {
      const n = parseFloat(v);
      return sum + (isNaN(n) ? 0 : n);
    }, 0);
    return total ? total.toFixed(2) : "";
  }, [poConsumption]);

  // Total Powder Coated On Date = Total Sq Ft + Rework Sq Ft (auto, readonly)
  const totalPowderCoated = useMemo(() => {
    const a = parseFloat(totalSqFt);
    const b = parseFloat(reworkSqFt);
    const sum = (isNaN(a) ? 0 : a) + (isNaN(b) ? 0 : b);
    return sum ? sum.toFixed(2) : "";
  }, [totalSqFt, reworkSqFt]);

  // Fetch production settings (Sq Ft field lock)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/production/settings`, auth());
        setSqftLocked(!!r.data?.sqft_locked);
      } catch (_) { /* non-blocking */ }
    })();
  }, []);

  // Auto-calculate Total / Rework Sq Ft from the selected products using the BOM
  // area formula (2 × L × W across the product's components). Total uses accepted
  // qty, rework uses rework qty. Products without a catalog match/dimensions are
  // skipped and surfaced via sqftInfo so the user can adjust manually.
  const recomputeSqft = async (itemsArr = items) => {
    const payloadItems = (itemsArr || [])
      .filter((it) => (it.product_code || "").trim())
      .map((it) => ({
        product_code: it.product_code,
        accepted_qty: Number(it.accepted_qty || 0),
        rework_qty: Number(it.rework_qty || 0),
      }));
    if (payloadItems.length === 0) {
      setSqftInfo(null);
      return;
    }
    setSqftComputing(true);
    try {
      const r = await axios.post(`${API}/production/compute-sqft`, { items: payloadItems }, auth());
      const d = r.data || {};
      setTotalSqFt(d.total_sq_ft != null ? String(d.total_sq_ft) : "");
      setReworkSqFt(d.rework_sq_ft != null ? String(d.rework_sq_ft) : "");
      setSqftInfo({ unmatched_count: d.unmatched_count || 0, unmatched_codes: d.unmatched_codes || [] });
      const areaMap = {};
      (d.lines || []).forEach((l) => { areaMap[l.product_code] = l.area_per_piece; });
      setSqftAreaMap(areaMap);
    } catch (_) {
      /* leave current values; never block the form */
    } finally {
      setSqftComputing(false);
    }
  };

  // Debounced live recompute whenever product / accepted / rework quantities change.
  const sqftSignature = useMemo(
    () => JSON.stringify((items || []).map((it) => [it.product_code, it.accepted_qty, it.rework_qty])),
    [items],
  );
  const skipAutoSqftRef = useRef(true);
  useEffect(() => {
    if (skipAutoSqftRef.current) { skipAutoSqftRef.current = false; return; }
    const t = setTimeout(() => { recomputeSqft(); }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sqftSignature]);

  // When report date changes: default plant start/stop to that date (9:15 AM - 5:30 PM)
  // if current values are empty or their date portion matches the OLD report date.
  const prevReportDateRef = useRef(reportDate);
  const initializedRef = useRef(false);
  useEffect(() => {
    const prev = prevReportDateRef.current;
    prevReportDateRef.current = reportDate;
    if (!initializedRef.current) { initializedRef.current = true; return; }
    if (!reportDate) return;
    setPlantStart((cur) => {
      const { date, time24 } = splitIso(cur);
      if (!cur) return defaultStart(reportDate);
      if (date === prev) return joinIso(reportDate, time24 || "09:15");
      return cur;
    });
    setPlantStop((cur) => {
      const { date, time24 } = splitIso(cur);
      if (!cur) return defaultStop(reportDate);
      if (date === prev) return joinIso(reportDate, time24 || "17:30");
      return cur;
    });
  }, [reportDate]);

  useEffect(() => { init(); /* eslint-disable-next-line */ }, [reportId]);

  const init = async () => {
    try {
      const [bRes, sRes, mRes] = await Promise.all([
        axios.get(`${API}/powder/batches`, auth()),
        axios.get(`${API}/powder/shades`, auth()),
        axios.get(`${API}/powder/makes`, auth()),
      ]);
      setBatches(bRes.data); setShades(sRes.data); setMakes(mRes.data);

      if (reportId) {
        const r = await axios.get(`${API}/production/reports/${reportId}`, auth());
        const rep = r.data;
        if (!(rep.status === "draft" || rep.status === "pending_approval") && mode === "edit") {
          toast.error("Only draft or pending-approval reports can be edited");
          navigate(`/safety/production-reports/${reportId}`, { replace: true });
          return;
        }
        setExistingStatus(rep.status);
        setReportDate(rep.report_date || todayStr());
        setPlantStart(rep.plant_started_at || "");
        setPlantStop(rep.plant_stopped_at || "");
        setManpower(rep.manpower_count ?? "");
        setGasPretKg(rep.gas_consumption_pret_kg ?? "");
        setBatchId(rep.batch_id || "");
        setTotalSqFt(rep.total_sq_ft ?? "");
        setReworkSqFt(rep.rework_sq_ft ?? "");
        setNotes(rep.notes || "");
        // Restore working hours: consider edited if value diverges from computed from plant times
        if (rep.working_hours != null && rep.working_hours !== "") {
          const autoFromTimes = computeHours(rep.plant_started_at || "", rep.plant_stopped_at || "");
          if (!autoFromTimes || String(rep.working_hours) !== String(autoFromTimes)) {
            setWorkingHoursInput(String(rep.working_hours));
          }
        }
        // Fetch PO details for selected po_ids
        if ((rep.po_ids || []).length > 0) {
          const openRes = await axios.get(`${API}/production/open-pos?exclude_report_id=${reportId}`, auth());
          const map = Object.fromEntries(openRes.data.map(p => [p.id, p]));
          const selected = rep.po_ids.map(id => map[id] || {
            id, po_number: (rep.po_numbers || [])[0] || id, customer_name: (rep.customer_names || []).join(", "),
          });
          setSelectedPos(selected);
        }
        setItems((rep.items || []).map(it => ({
          ...it,
          accepted_qty_touched: Number(it.accepted_qty || 0) !== Number(it.coated_qty || 0),
          // Tag items as auto when loading a pallet-auto report so the table renders
          // the correct readonly cells (coated/accepted) while keeping rework editable.
          from_pallets: rep.source === "pallet_auto" || !!it.from_pallets || (it.pallet_ids && it.pallet_ids.length > 0),
        })));
        // Restore source + linkedPalletIds so subsequent saves don't lose auto-fill linkage
        setSource(rep.source || "manual");
        setLinkedPalletIds(rep.pallet_ids || []);
        // Don't clobber the saved Sq Ft with an auto-recompute the moment we load;
        // the auto-calc still runs live as soon as the user edits quantities/products.
        skipAutoSqftRef.current = true;
        // Back-fill pending_qty for existing items by querying the pending-products endpoint
        if ((rep.po_ids || []).length > 0 && (rep.items || []).length > 0) {
          try {
            const ids = (rep.po_ids || []).join(",");
            const pp = await axios.get(`${API}/production/pending-products?po_ids=${ids}&exclude_report_id=${reportId}`, auth());
            const pmap = {};
            (pp.data || []).forEach(p => { pmap[`${p.po_id}|${p.product_code}`] = p.pending_qty; });
            setItems(prev => prev.map(it => ({
              ...it,
              pending_qty: pmap[`${it.po_id}|${it.product_code}`] ?? it.pending_qty ?? 0,
            })));
          } catch (e) { /* non-fatal */ }
        }
        const cm = {};
        (rep.po_consumptions || []).forEach(c => {
          // po_id may be null/empty for the Unassigned bucket (pallet_auto)
          const key = c.po_id || "__unassigned__";
          cm[key] = c.consumption_kg;
        });
        setPoConsumption(cm);
      } else {
        // Prefill from query (?prefill_po=&prefill_code=&prefill_qty=)
        const prefPo = searchParams.get("prefill_po");
        const prefCode = searchParams.get("prefill_code");
        const prefQty = searchParams.get("prefill_qty");
        if (prefPo && prefCode) {
          try {
            const openRes = await axios.get(`${API}/production/open-pos`, auth());
            const po = (openRes.data || []).find((p) => p.id === prefPo);
            if (po) setSelectedPos([po]);
            const ppRes = await axios.get(`${API}/production/pending-products?po_ids=${prefPo}`, auth());
            const product = (ppRes.data || []).find((p) => p.product_code === prefCode);
            if (po && product) {
              setItems([{
                po_id: po.id,
                po_number: po.po_number,
                product_code: product.product_code,
                description: product.description,
                order_qty: product.order_qty,
                pending_qty: product.pending_qty,
                coated_qty: prefQty ? Number(prefQty) : 0,
                rework_qty: 0,
                accepted_qty: prefQty ? Number(prefQty) : 0,
                accepted_qty_touched: false,
              }]);
              toast.success(`Prefilled ${product.product_code} from PO ${po.po_number}`);
            }
          } catch { /* non-fatal */ }
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load form data");
    } finally {
      setLoading(false);
    }
  };

  // ============ PO Picker ============
  const openPoPicker = async () => {
    try {
      const qs = reportId ? `?exclude_report_id=${reportId}` : "";
      const res = await axios.get(`${API}/production/open-pos${qs}`, auth());
      setOpenPos(res.data);
      // pre-check already-selected
      const pre = {};
      selectedPos.forEach(p => { pre[p.id] = true; });
      setPoPickerSelected(pre);
      setPoPickerOpen(true);
    } catch (e) { toast.error("Failed to load POs"); }
  };
  const filteredOpenPos = openPos
    .filter(p => {
      if (!poSearch) return true;
      const q = poSearch.toLowerCase();
      return (p.po_number || "").toLowerCase().includes(q) ||
             (p.customer_name || "").toLowerCase().includes(q) ||
             (p.ship_to_name || "").toLowerCase().includes(q);
    })
    .sort((a, b) => (a.po_number || "").localeCompare(b.po_number || ""));
  const confirmPoPicker = () => {
    const chosen = openPos.filter(p => poPickerSelected[p.id]);
    setSelectedPos(chosen);
    // Drop items/consumption for deselected POs
    const chosenIds = new Set(chosen.map(p => p.id));
    setItems(prev => prev.filter(it => chosenIds.has(it.po_id)));
    setPoConsumption(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (k === "__unassigned__" || chosenIds.has(k)) next[k] = v;
      });
      return next;
    });
    setPoPickerOpen(false);
  };

  // ============ Product Picker ============
  const openProductPicker = async () => {
    if (selectedPos.length === 0) { toast.error("Select at least one PO first"); return; }
    try {
      const ids = selectedPos.map(p => p.id).join(",");
      const ex = reportId ? `&exclude_report_id=${reportId}` : "";
      const res = await axios.get(`${API}/production/pending-products?po_ids=${ids}${ex}`, auth());
      setPendingProducts(res.data);
      setProductPickerSelected({});
      setProductPickerOpen(true);
    } catch (e) { toast.error("Failed to load products"); }
  };
  const confirmProductPicker = () => {
    const toAdd = pendingProducts
      .filter(p => productPickerSelected[`${p.po_id}|${p.product_code}`])
      .map(p => ({
        po_id: p.po_id,
        po_number: p.po_number,
        product_code: p.product_code,
        description: p.description,
        order_qty: p.order_qty,
        pending_qty: p.pending_qty,
        coated_qty: 0,
        rework_qty: 0,
        accepted_qty: 0,
        accepted_qty_touched: false,
      }));
    setItems(prev => {
      const existing = new Set(prev.map(x => `${x.po_id}|${x.product_code}`));
      const merged = [...prev];
      toAdd.forEach(n => { if (!existing.has(`${n.po_id}|${n.product_code}`)) merged.push(n); });
      return merged;
    });
    setProductPickerOpen(false);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const v = value === "" ? 0 : parseInt(value, 10);
      const num = isNaN(v) ? 0 : Math.max(0, v);
      const updated = { ...it, [field]: num };
      if (field === "coated_qty") {
        // Cap rework to coated
        if ((updated.rework_qty || 0) > num) updated.rework_qty = num;
        // Sync accepted with coated unless user edited it
        if (!updated.accepted_qty_touched) updated.accepted_qty = num;
      }
      if (field === "rework_qty") {
        // Rework is logged for traceability but does NOT reduce accepted qty.
        // Per user spec: coated = accepted; rework runs alongside without subtracting.
        updated.rework_qty = Math.min(num, updated.coated_qty || 0);
      }
      if (field === "accepted_qty") {
        updated.accepted_qty_touched = true;
      }
      return updated;
    }));
  };
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // ============ Feature D P2 — Pallet picker (operator-driven, session-less) ============
  const openPalletPicker = async () => {
    setPalletPickerOpen(true);
    setPalletPickerLoading(true);
    setPickedPalletIds({});
    try {
      const params = new URLSearchParams();
      if (palletFilterFrom) params.set("paired_from", `${palletFilterFrom}T00:00:00`);
      if (palletFilterTo) params.set("paired_to", `${palletFilterTo}T23:59:59`);
      const res = await axios.get(
        `${API}/production-reports/candidate-pallets?${params.toString()}`,
        auth(),
      );
      setCandidatePallets(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load candidate pallets");
      setCandidatePallets([]);
    } finally {
      setPalletPickerLoading(false);
    }
  };

  const reloadCandidates = async () => {
    setPalletPickerLoading(true);
    try {
      const params = new URLSearchParams();
      if (palletFilterFrom) params.set("paired_from", `${palletFilterFrom}T00:00:00`);
      if (palletFilterTo) params.set("paired_to", `${palletFilterTo}T23:59:59`);
      const res = await axios.get(
        `${API}/production-reports/candidate-pallets?${params.toString()}`,
        auth(),
      );
      setCandidatePallets(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load candidate pallets");
    } finally {
      setPalletPickerLoading(false);
    }
  };

  const filteredCandidates = candidatePallets.filter((p) => {
    if (!palletSearch) return true;
    const q = palletSearch.toLowerCase();
    return (
      (p.id || "").toLowerCase().includes(q) ||
      (p.product_code || "").toLowerCase().includes(q) ||
      (p.po_no || "").toLowerCase().includes(q) ||
      (p.customer_name || "").toLowerCase().includes(q)
    );
  });

  const applyPickedPallets = async () => {
    const ids = Object.keys(pickedPalletIds).filter((k) => pickedPalletIds[k]);
    if (ids.length === 0) {
      toast.error("Pick at least one pallet first");
      return;
    }
    setAutoPrefilling(true);
    try {
      const res = await axios.get(
        `${API}/production-reports/auto-prefill?pallet_ids=${encodeURIComponent(ids.join(","))}`,
        auth(),
      );
      const data = res.data || {};
      const palletItems = data.items || [];
      const mapped = palletItems.map((it) => ({
        po_id: it.po_id || "",
        po_number: it.po_number || "",
        product_code: it.product_code,
        description: it.description || "",
        order_qty: it.order_qty || 0,
        pending_qty: it.pending_qty || 0,
        coated_qty: it.coated_qty || 0,
        rework_qty: 0,
        accepted_qty: it.accepted_qty || 0,
        accepted_qty_touched: true,
        from_pallets: true,
        pallet_count: it.pallet_count || (it.pallet_ids || []).length,
        pallet_ids: it.pallet_ids || [],
      }));
      const palletPoIds = (data.po_ids || []).filter(Boolean);
      if (palletPoIds.length > 0) {
        try {
          const poRes = await axios.get(`${API}/po/list`, auth());
          const map = Object.fromEntries((poRes.data || []).map(p => [p.id, p]));
          const palletPos = palletPoIds
            .map(id => map[id] || {
              // Fallback: synthesize a minimal PO doc from the auto-prefill items so
              // per-PO Consumption + chips still render even if /po/list is missing
              // the doc (e.g. permission-trimmed list).
              id,
              po_number: (palletItems.find(it => it.po_id === id) || {}).po_number || id,
              customer_name: "",
            })
            .filter(Boolean);
          setSelectedPos(prev => {
            const existing = new Set(prev.map(p => p.id));
            const merged = [...prev];
            palletPos.forEach(p => { if (!existing.has(p.id)) merged.push(p); });
            return merged;
          });
        } catch {
          // Even if /po/list fails, derive minimal PO stubs from the items so the
          // per-PO Consumption inputs still render.
          const stubs = palletPoIds.map(id => ({
            id,
            po_number: (palletItems.find(it => it.po_id === id) || {}).po_number || id,
            customer_name: "",
          }));
          setSelectedPos(prev => {
            const existing = new Set(prev.map(p => p.id));
            const merged = [...prev];
            stubs.forEach(p => { if (!existing.has(p.id)) merged.push(p); });
            return merged;
          });
        }
      }
      setItems(mapped);
      setLinkedPalletIds(data.pallet_ids || []);
      setSource("pallet_auto");
      setPalletPickerOpen(false);
      toast.success(`Added ${data.pallet_count} pallets across ${mapped.length} product lines`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not auto-prefill from pallets");
    } finally {
      setAutoPrefilling(false);
    }
  };

  const clearAutoFill = () => {
    setItems([]);
    setLinkedPalletIds([]);
    setSource("manual");
    // Drop Unassigned bucket from per-PO consumption (only valid in pallet_auto mode)
    setPoConsumption(prev => {
      const next = { ...prev };
      delete next.__unassigned__;
      return next;
    });
  };

  // ============ Add New Batch inline ============
  const addShadeInline = async () => {
    if (!newShadeInline.trim()) return;
    try {
      const res = await axios.post(`${API}/powder/shades`, { name: newShadeInline.trim() }, auth());
      setShades(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewBatchForm(f => ({ ...f, shade_id: res.data.id }));
      setNewShadeInline("");
      toast.success("Shade added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addMakeInline = async () => {
    if (!newMakeInline.trim()) return;
    try {
      const res = await axios.post(`${API}/powder/makes`, { name: newMakeInline.trim() }, auth());
      setMakes(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewBatchForm(f => ({ ...f, make_id: res.data.id }));
      setNewMakeInline("");
      toast.success("Make added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveNewBatch = async () => {
    if (!newBatchForm.batch_no.trim() || !newBatchForm.shade_id || !newBatchForm.make_id) {
      toast.error("All fields required"); return;
    }
    try {
      const res = await axios.post(`${API}/powder/batches`, newBatchForm, auth());
      setBatches(prev => [res.data, ...prev]);
      setBatchId(res.data.id);
      setNewBatchOpen(false);
      setNewBatchForm({ batch_no: "", shade_id: "", make_id: "" });
      toast.success("Batch added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // ============ Save / Submit ============
  const buildPayload = () => ({
    report_date: reportDate,
    plant_started_at: plantStart || null,
    plant_stopped_at: plantStop || null,
    working_hours: workingHours !== "" ? parseFloat(workingHours) : null,
    manpower_count: manpower !== "" ? parseInt(manpower, 10) : null,
    gas_consumption_pret_kg: gasPretKg !== "" ? parseFloat(gasPretKg) : null,
    po_ids: selectedPos.map(p => p.id),
    items: items.map(it => ({
      po_id: it.po_id,
      po_number: it.po_number,
      product_code: it.product_code,
      description: it.description,
      order_qty: it.order_qty || 0,
      coated_qty: it.coated_qty || 0,
      rework_qty: it.rework_qty || 0,
      accepted_qty: it.accepted_qty ?? (it.coated_qty || 0),
    })),
    batch_id: batchId || null,
    withdrawn_kg: withdrawnKg !== "" ? parseFloat(withdrawnKg) : null,
    po_consumptions: Object.entries(poConsumption)
      .map(([key, kg]) => {
        const isUnassigned = key === "__unassigned__";
        return {
          po_id: isUnassigned ? null : key,
          po_number: isUnassigned
            ? "(Unassigned)"
            : ((selectedPos.find(p => p.id === key) || {}).po_number || ""),
          consumption_kg: parseFloat(kg) || 0,
        };
      })
      .sort((a, b) => (a.po_number || "").localeCompare(b.po_number || "")),
    total_sq_ft: totalSqFt !== "" ? parseFloat(totalSqFt) : null,
    rework_sq_ft: reworkSqFt !== "" ? parseFloat(reworkSqFt) : null,
    total_powder_coated_on_date: totalPowderCoated !== "" ? parseFloat(totalPowderCoated) : null,
    notes: notes || null,
    edit_reason: existingStatus === "pending_approval" ? (editReason || null) : null,
    source,
    pallet_ids: linkedPalletIds,
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      let id = reportId;
      if (id && mode === "edit") {
        await axios.put(`${API}/production/reports/${id}`, payload, auth());
      } else {
        const res = await axios.post(`${API}/production/reports`, payload, auth());
        id = res.data.id;
      }
      if (existingStatus === "pending_approval") {
        toast.success("Changes saved. Report remains pending approval.");
        setReasonDialogOpen(false);
        setEditReason("");
        navigate(`/safety/production-reports/${id}`);
      } else {
        toast.success("Draft saved");
        navigate(`/safety/production-reports/${id}/edit`, { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const submit = async () => {
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      let id = reportId;
      if (id && mode === "edit") {
        await axios.put(`${API}/production/reports/${id}`, payload, auth());
      } else {
        const res = await axios.post(`${API}/production/reports`, payload, auth());
        id = res.data.id;
      }
      // Only submit if not already submitted
      if (existingStatus !== "pending_approval") {
        await axios.post(`${API}/production/reports/${id}/submit`, {}, auth());
      }
      toast.success("Submitted for approval");
      navigate("/safety/production-reports");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
    </div>;
  }

  return (
    <div data-testid="production-report-form">
      <Toaster position="top-right" richColors />
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/safety/production-reports")} className="text-white hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold">{mode === "edit" ? "Edit" : "New"} Daily Production Report</h1>
          </div>
          <UserMenu user={user} onLogout={onLogout} variant="header" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {existingStatus === "pending_approval" && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3" data-testid="pending-edit-banner">
            <div className="w-8 h-8 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center font-bold shrink-0">!</div>
            <div className="text-sm text-amber-900">
              <div className="font-semibold">Editing a pending-approval report</div>
              Changes you save here will remain in pending-approval status and will be visible to the approver. The report will be marked "edited after submission".
            </div>
          </div>
        )}

        {/* Step 1: Header */}
        <section className="bg-white rounded-lg border shadow-sm p-5">
          <h2 className="font-semibold mb-3">Header</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} data-testid="report-date" />
            </div>
            <div>
              <Label>Plant Started at</Label>
              <DateTimeAmPm value={plantStart} onChange={setPlantStart} testId="plant-start" />
              <p className="text-xs text-gray-500 mt-1">Defaults to 9:15 AM on report date; can be previous evening for night shift.</p>
            </div>
            <div>
              <Label>Plant Stopped at</Label>
              <DateTimeAmPm value={plantStop} onChange={setPlantStop} testId="plant-stop" />
              <p className="text-xs text-gray-500 mt-1">Defaults to 5:30 PM on report date.</p>
            </div>
            <div>
              <Label>Working Hours</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={workingHours}
                  onChange={(e) => setWorkingHoursInput(e.target.value)}
                  placeholder="Auto from plant times"
                  data-testid="working-hours"
                />
                {workingHoursEdited && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setWorkingHoursInput("")}
                    data-testid="reset-working-hours"
                    title="Reset to computed value"
                  >
                    Reset
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {workingHoursEdited
                  ? `Overridden. Computed from plant times: ${computedHours || "-"}`
                  : "Auto-computed from plant start/stop. Editable if actual hours are lower."}
              </p>
            </div>
            <div>
              <Label>Manpower</Label>
              <Input type="number" min="0" value={manpower} onChange={(e) => setManpower(e.target.value)} data-testid="manpower" />
            </div>
            <div>
              <Label>Gas Consumption (Kg)</Label>
              <Input type="number" step="0.01" min="0" value={gasPretKg} onChange={(e) => setGasPretKg(e.target.value)} placeholder="e.g., 357.5" data-testid="gas-pret-kg" />
            </div>
          </div>
        </section>

        {/* Step 2: POs — hidden in pallet-auto mode (POs are derived from picked pallets) */}
        {source !== "pallet_auto" ? (
          <section className="bg-white rounded-lg border shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">SO No. / Purchase Orders</h2>
              <Button onClick={openPoPicker} variant="outline" data-testid="select-pos-btn">
                <ListPlus className="w-4 h-4 mr-1" /> Select POs
              </Button>
            </div>
            {selectedPos.length === 0 ? (
              <p className="text-gray-500 text-sm">No POs selected yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[...selectedPos].sort((a, b) => (a.po_number || "").localeCompare(b.po_number || "")).map(p => (
                  <div key={p.id} className="border rounded p-3 text-sm" data-testid={`selected-po-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{p.po_number}</span>
                      <span className="text-xs text-gray-500">{p.status || "pending"}</span>
                    </div>
                    <div className="text-gray-600">Customer: {p.customer_name || <span className="text-amber-600">Not assigned</span>}</div>
                    {p.ship_to_name && p.ship_to_name !== p.customer_name && (
                      <div className="text-gray-500 text-xs"><Truck className="w-3 h-3 inline mr-1" />Ship to: {p.ship_to_name}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 text-sm text-gray-600">
              <span className="font-semibold">Customer:</span> {Array.from(new Set(selectedPos.map(p => p.customer_name).filter(Boolean))).join(", ") || "-"}
            </div>
          </section>
        ) : (
          <section className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="auto-po-summary">
            <div className="text-sm">
              <span className="font-semibold text-orange-900">📡 Auto from pallets — POs derived from the picked pallets:</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedPos.length === 0 ? (
                  <span className="text-orange-700 italic">All pallets are Unassigned</span>
                ) : (
                  selectedPos.map(p => (
                    <span key={p.id} className="inline-flex items-center gap-1 bg-white border border-orange-200 rounded-full px-2 py-1 text-xs font-mono" data-testid={`auto-po-chip-${p.id}`}>
                      {p.po_number}
                      <span className="text-gray-500">· {p.customer_name || "—"}</span>
                    </span>
                  ))
                )}
              </div>
            </div>
          </section>
        )}

        {/* Step 3: Items */}
        <section className="bg-white rounded-lg border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold">Production Status (Line Items)</h2>
              {source === "pallet_auto" && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 border border-orange-200"
                  data-testid="auto-source-badge"
                >
                  📡 Auto from {linkedPalletIds.length} pallets
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {source === "pallet_auto" ? (
                <Button onClick={clearAutoFill} variant="outline" size="sm" data-testid="clear-auto-btn">
                  Clear & enter manually
                </Button>
              ) : (
                <Button
                  onClick={openPalletPicker}
                  variant="outline"
                  size="sm"
                  disabled={autoPrefilling || items.length > 0}
                  title={items.length > 0 ? "Clear existing rows first" : "Pick PRODUCED pallets to include"}
                  data-testid="auto-prefill-btn"
                >
                  📦 Pick from pallets
                </Button>
              )}
              <Button onClick={openProductPicker} variant="outline" data-testid="add-products-btn" disabled={selectedPos.length === 0 || source === "pallet_auto"}>
                <Plus className="w-4 h-4 mr-1" /> Add Products
              </Button>
            </div>
          </div>
          {source === "pallet_auto" && (
            <p className="text-xs text-gray-500 mb-2">
              Qty fields are read-only because they come from verified pallet scans. To fix a count, void the pallet scan in the Pallets module rather than editing the report.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border p-2 text-left" colSpan="5">Order Description</th>
                  <th className="border p-2 text-left" colSpan="4">Production Status</th>
                  <th className="border p-2"></th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="border p-2 text-left">Product Code</th>
                  <th className="border p-2 text-left">Description</th>
                  <th className="border p-2 text-left">PO No.</th>
                  <th className="border p-2 text-right">Order Qty</th>
                  <th className="border p-2 text-right">Pending Qty</th>
                  <th className="border p-2 text-right">Coated Qty</th>
                  <th className="border p-2 text-right">Rework Qty</th>
                  <th className="border p-2 text-right">Accepted OK Qty</th>
                  <th className="border p-2 text-right">Sq Ft</th>
                  <th className="border p-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan="10" className="p-6 text-center text-gray-500" data-testid="no-items">No products added. Click "Add Products" after selecting POs.</td></tr>}
                {items.map((it, idx) => {
                  const isAuto = !!it.from_pallets;
                  return (
                    <tr key={`${it.po_id || "unassigned"}|${it.product_code}|${idx}`} data-testid={`item-row-${idx}`} className={isAuto ? "bg-orange-50/40" : ""}>
                      <td className="border p-2">{it.product_code}</td>
                      <td className="border p-2">{it.description}</td>
                      <td className="border p-2">
                        {it.po_number || <span className="text-gray-400 italic">Unassigned</span>}
                        {isAuto && (
                          <div className="text-[10px] text-gray-500 mt-0.5">{it.pallet_count} pallets</div>
                        )}
                      </td>
                      <td className="border p-2 text-right">{it.order_qty || "—"}</td>
                      <td className="border p-2 text-right" data-testid={`pending-qty-${idx}`}>
                        <div>{it.pending_qty || "—"}</div>
                        {!isAuto && it.pending_qty != null && Number(it.coated_qty || 0) > 0 && (() => {
                          const after = Number(it.pending_qty) - Number(it.coated_qty || 0);
                          const over = after < 0;
                          return (
                            <div className={`text-xs mt-0.5 ${over ? "text-red-600 font-medium" : "text-gray-500"}`} data-testid={`pending-after-${idx}`}>
                              After: {after}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="border p-1">
                        {isAuto ? (
                          <div className="text-right pr-2 font-semibold">{it.coated_qty}</div>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            value={it.coated_qty}
                            onChange={(e) => updateItem(idx, "coated_qty", e.target.value)}
                            onBlur={() => {
                              if (it.pending_qty != null && Number(it.coated_qty || 0) > Number(it.pending_qty)) {
                                toast.warning(`Coated Qty (${it.coated_qty}) exceeds Pending Qty (${it.pending_qty}) for ${it.product_code}`);
                              }
                            }}
                            className="text-right"
                            data-testid={`coated-qty-${idx}`}
                          />
                        )}
                      </td>
                      <td className="border p-1">
                        <Input
                          type="number"
                          min="0"
                          max={it.coated_qty}
                          value={it.rework_qty ?? 0}
                          onChange={(e) => updateItem(idx, "rework_qty", e.target.value)}
                          className="text-right"
                          data-testid={`rework-qty-${idx}`}
                        />
                      </td>
                      <td className="border p-1">
                        {isAuto ? (
                          <div className="text-right pr-2 font-semibold">{it.accepted_qty}</div>
                        ) : (
                          <Input type="number" min="0" value={it.accepted_qty ?? 0} onChange={(e) => updateItem(idx, "accepted_qty", e.target.value)} className="text-right font-semibold" data-testid={`accepted-qty-${idx}`} />
                        )}
                      </td>
                      {(() => {
                        const areaPc = sqftAreaMap[it.product_code];
                        const hasArea = areaPc != null && areaPc > 0;
                        const acc = Number(it.accepted_qty || 0);
                        const rew = Number(it.rework_qty || 0);
                        const accSqft = hasArea ? acc * areaPc : 0;
                        const rewSqft = hasArea ? rew * areaPc : 0;
                        return (
                          <td className="border p-1 text-right" data-testid={`line-sqft-${idx}`}>
                            {hasArea ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 text-sky-700 hover:underline font-medium tabular-nums"
                                    data-testid={`line-sqft-trigger-${idx}`}
                                  >
                                    {accSqft.toFixed(2)}
                                    <Info className="w-3 h-3 text-gray-400" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 text-xs" align="end" data-testid={`line-sqft-popover-${idx}`}>
                                  <div className="font-semibold mb-1.5">{it.product_code} — Sq Ft breakdown</div>
                                  <div className="flex justify-between py-0.5">
                                    <span className="text-gray-500">Area / piece</span>
                                    <span className="tabular-nums">{areaPc.toFixed(4)} ft²</span>
                                  </div>
                                  <div className="flex justify-between py-0.5">
                                    <span className="text-gray-500">Accepted</span>
                                    <span className="tabular-nums">{acc} × {areaPc.toFixed(4)} = <b>{accSqft.toFixed(2)}</b></span>
                                  </div>
                                  <div className="flex justify-between py-0.5 border-t mt-1 pt-1">
                                    <span className="text-gray-500">Rework</span>
                                    <span className="tabular-nums">{rew} × {areaPc.toFixed(4)} = <b>{rewSqft.toFixed(2)}</b></span>
                                  </div>
                                  <p className="text-[10px] text-gray-400 mt-2">Same formula as the BOM (2 × L × W across components).</p>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <span className="text-amber-600 text-xs inline-flex items-center gap-1" title="No catalog dimensions found for this product" data-testid={`line-sqft-na-${idx}`}>
                                <Info className="w-3 h-3" /> n/a
                              </span>
                            )}
                          </td>
                        );
                      })()}
                      <td className="border p-2 text-center">
                        {!isAuto && (
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Step 4: Powder */}
        <section className="bg-white rounded-lg border shadow-sm p-5">
          <h2 className="font-semibold mb-3">Powder Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label>Batch No</Label>
              <div className="flex gap-2">
                <Select value={batchId} onValueChange={setBatchId}>
                  <SelectTrigger data-testid="batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                  <SelectContent>
                    {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.batch_no} — {b.shade_name} / {b.make_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => setNewBatchOpen(true)} data-testid="add-batch-inline-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>Shade</Label>
              <Input readOnly className="bg-gray-50" value={batches.find(b => b.id === batchId)?.shade_name || ""} data-testid="shade-readonly" />
            </div>
            <div>
              <Label>Make</Label>
              <Input readOnly className="bg-gray-50" value={batches.find(b => b.id === batchId)?.make_name || ""} data-testid="make-readonly" />
            </div>
            <div>
              <Label>Total Withdrawn (kg)</Label>
              <Input
                type="number"
                step="0.01"
                value={withdrawnKg}
                readOnly
                className="bg-gray-50"
                data-testid="withdrawn-kg"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-total of per-PO consumption below.</p>
            </div>
          </div>
          {(selectedPos.length > 0 || (source === "pallet_auto" && items.some(it => !it.po_id))) && (
            <div className={`mt-4 ${source === "pallet_auto" ? "bg-orange-50 border border-orange-200 rounded-lg p-3" : ""}`}>
              <h3 className={`font-medium text-sm mb-2 ${source === "pallet_auto" ? "text-orange-900" : ""}`}>
                {source === "pallet_auto"
                  ? "📡 Per-PO Consumption (auto from pallets)"
                  : "Per-PO Consumption (kg)"}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[...selectedPos].sort((a, b) => (a.po_number || "").localeCompare(b.po_number || "")).map(p => (
                  <div key={p.id}>
                    <Label className="text-xs">{p.po_number}</Label>
                    <Input type="number" step="0.01" min="0" value={poConsumption[p.id] ?? ""} onChange={(e) => setPoConsumption({ ...poConsumption, [p.id]: e.target.value })} data-testid={`consumption-${p.id}`} />
                  </div>
                ))}
                {source === "pallet_auto" && items.some(it => !it.po_id) && (
                  <div data-testid="consumption-unassigned-wrap">
                    <Label className="text-xs italic text-orange-800">Unassigned</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={poConsumption["__unassigned__"] ?? ""}
                      onChange={(e) => setPoConsumption({ ...poConsumption, ["__unassigned__"]: e.target.value })}
                      data-testid="consumption-unassigned"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Step 5: Footer */}
        <section className="bg-white rounded-lg border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Totals & Notes</h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => recomputeSqft()}
              disabled={sqftComputing}
              title="Recalculate Total & Rework Sq Ft from the selected products"
              data-testid="recalc-sqft-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${sqftComputing ? "animate-spin" : ""}`} />
              {sqftComputing ? "Calculating…" : "Recalculate Sq Ft"}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Total Powder Coated On Date (Sq Ft)</Label>
              <Input
                type="number"
                step="0.01"
                value={totalPowderCoated}
                readOnly
                className="bg-gray-50"
                data-testid="total-powder-coated"
              />
              <p className="text-xs text-gray-500 mt-1">Auto = Total Sq Ft + Rework Sq Ft.</p>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                Total Sq Ft
                {sqftLocked && <Lock className="w-3 h-3 text-gray-400" title="Locked by admin — auto-calculated" />}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={totalSqFt}
                onChange={(e) => setTotalSqFt(e.target.value)}
                readOnly={sqftLocked}
                className={sqftLocked ? "bg-gray-50" : ""}
                data-testid="total-sqft"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                Rework Sq Ft
                {sqftLocked && <Lock className="w-3 h-3 text-gray-400" title="Locked by admin — auto-calculated" />}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={reworkSqFt}
                onChange={(e) => setReworkSqFt(e.target.value)}
                readOnly={sqftLocked}
                className={sqftLocked ? "bg-gray-50" : ""}
                data-testid="rework-sqft"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2" data-testid="sqft-helper">
            Auto-calculated from the selected products (accepted qty × surface area, same formula as the BOM).
            {sqftLocked ? " Fields are locked by an admin." : " You can override the values manually."}
          </p>
          {sqftInfo && sqftInfo.unmatched_count > 0 && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="sqft-unmatched-note">
              ⚠ {sqftInfo.unmatched_count} product{sqftInfo.unmatched_count === 1 ? "" : "s"} without catalog dimensions were
              excluded from the auto-total{sqftInfo.unmatched_codes?.length ? ` (${sqftInfo.unmatched_codes.slice(0, 5).join(", ")}${sqftInfo.unmatched_codes.length > 5 ? "…" : ""})` : ""}.
              {!sqftLocked && " Add them manually if needed."}
            </p>
          )}
          <div className="mt-4">
            <Label>Notes</Label>
            <Textarea rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="notes-input" />
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Sign Dpt Incharge</Label>
              <Input readOnly className="bg-gray-50" value={user?.name || user?.email || ""} data-testid="sign-dpt-incharge" />
            </div>
            <div>
              <Label>Sign DH (set upon approval)</Label>
              <Input readOnly className="bg-gray-50" value="— filled by Dept Head —" />
            </div>
          </div>
        </section>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          {existingStatus === "pending_approval" ? (
            <Button
              onClick={() => setReasonDialogOpen(true)}
              disabled={saving || items.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-pending-btn"
            >
              <Save className="w-4 h-4 mr-1" /> Save Changes
            </Button>
          ) : (
            <>
              <Button onClick={saveDraft} variant="outline" disabled={saving} data-testid="save-draft-btn">
                <Save className="w-4 h-4 mr-1" /> Save Draft
              </Button>
              <Button onClick={submit} disabled={saving || items.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-btn">
                <Send className="w-4 h-4 mr-1" /> Submit for Approval
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Reason for change dialog (pending-approval edits) */}
      <Dialog open={reasonDialogOpen} onOpenChange={(o) => { setReasonDialogOpen(o); if (!o) setEditReason(""); }}>
        <DialogContent data-testid="edit-reason-dialog">
          <DialogHeader>
            <DialogTitle>Save changes to pending-approval report</DialogTitle>
            <DialogDescription>
              Optionally describe what was corrected. The reason will be visible to the approver alongside the field-level diff.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows="3"
            placeholder="e.g. Corrected accepted qty after re-inspection (optional)"
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            data-testid="edit-reason-input"
            maxLength={500}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReasonDialogOpen(false); setEditReason(""); }} data-testid="edit-reason-cancel">
              Cancel
            </Button>
            <Button onClick={saveDraft} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-reason-save">
              <Save className="w-4 h-4 mr-1" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO Picker Dialog */}
      <Dialog open={poPickerOpen} onOpenChange={setPoPickerOpen}>
        <DialogContent className="max-w-4xl" data-testid="po-picker-dialog">
          <DialogHeader>
            <DialogTitle>Select Purchase Orders</DialogTitle>
            <DialogDescription>Only POs with pending production qty are shown.</DialogDescription>
          </DialogHeader>
          <div className="mb-2">
            <Input placeholder="Search PO / Customer / Ship To" value={poSearch} onChange={(e) => setPoSearch(e.target.value)} data-testid="po-search" />
          </div>
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 w-10"></th>
                  <th className="p-2 text-left">PO No</th>
                  <th className="p-2 text-left">Customer</th>
                  <th className="p-2 text-left">Ship To</th>
                  <th className="p-2 text-right">Products</th>
                  <th className="p-2 text-right">Order Qty</th>
                  <th className="p-2 text-right">Pending Qty</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredOpenPos.length === 0 && <tr><td colSpan="8" className="p-4 text-center text-gray-500">No open POs found.</td></tr>}
                {filteredOpenPos.map(p => (
                  <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`po-option-${p.id}`}>
                    <td className="p-2">
                      <Checkbox checked={!!poPickerSelected[p.id]} onCheckedChange={(v) => setPoPickerSelected({ ...poPickerSelected, [p.id]: !!v })} data-testid={`po-checkbox-${p.id}`} />
                    </td>
                    <td className="p-2 font-medium">{p.po_number}</td>
                    <td className="p-2">{p.customer_name || <span className="text-amber-600 text-xs">Not assigned</span>}</td>
                    <td className="p-2 text-gray-600">{p.ship_to_name || "-"}</td>
                    <td className="p-2 text-right">{p.product_count}</td>
                    <td className="p-2 text-right">{p.total_ordered ?? "-"}</td>
                    <td className="p-2 text-right">{p.total_pending}</td>
                    <td className="p-2">{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoPickerOpen(false)}>Cancel</Button>
            <Button onClick={confirmPoPicker} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-po-picker">Add Selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Picker Dialog */}
      <Dialog open={productPickerOpen} onOpenChange={setProductPickerOpen}>
        <DialogContent className="max-w-4xl" data-testid="product-picker-dialog">
          <DialogHeader>
            <DialogTitle>Select Products to Add</DialogTitle>
            <DialogDescription>Products with pending qty from the selected POs.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 w-10"></th>
                  <th className="p-2 text-left">PO No</th>
                  <th className="p-2 text-left">Product Code</th>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-right">Order Qty</th>
                  <th className="p-2 text-right">Pending Qty</th>
                </tr>
              </thead>
              <tbody>
                {pendingProducts.length === 0 && <tr><td colSpan="6" className="p-4 text-center text-gray-500">No pending products.</td></tr>}
                {pendingProducts.map(p => {
                  const key = `${p.po_id}|${p.product_code}`;
                  const already = items.some(it => it.po_id === p.po_id && it.product_code === p.product_code);
                  return (
                    <tr key={key} className={`border-t ${already ? "opacity-50" : "hover:bg-gray-50"}`} data-testid={`product-option-${key}`}>
                      <td className="p-2">
                        <Checkbox
                          disabled={already}
                          checked={!!productPickerSelected[key]}
                          onCheckedChange={(v) => setProductPickerSelected({ ...productPickerSelected, [key]: !!v })}
                          data-testid={`product-checkbox-${key}`}
                        />
                      </td>
                      <td className="p-2">{p.po_number}</td>
                      <td className="p-2 font-medium">{p.product_code}</td>
                      <td className="p-2 text-gray-600">{p.description}</td>
                      <td className="p-2 text-right">{p.order_qty}</td>
                      <td className="p-2 text-right">{p.pending_qty}{already && <span className="text-xs text-gray-400 ml-2">(added)</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductPickerOpen(false)}>Cancel</Button>
            <Button onClick={confirmProductPicker} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-product-picker">Add Selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New batch inline dialog */}
      <Dialog open={newBatchOpen} onOpenChange={setNewBatchOpen}>
        <DialogContent data-testid="new-batch-dialog">
          <DialogHeader>
            <DialogTitle>Add New Powder Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Batch Number *</Label>
              <Input value={newBatchForm.batch_no} onChange={(e) => setNewBatchForm({ ...newBatchForm, batch_no: e.target.value })} data-testid="new-batch-no-input" />
            </div>
            <div>
              <Label>Shade *</Label>
              <div className="flex gap-2">
                <Select value={newBatchForm.shade_id} onValueChange={(v) => setNewBatchForm({ ...newBatchForm, shade_id: v })}>
                  <SelectTrigger data-testid="new-batch-shade-select"><SelectValue placeholder="Pick shade" /></SelectTrigger>
                  <SelectContent>
                    {shades.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 mt-1">
                <Input placeholder="Or add new shade" value={newShadeInline} onChange={(e) => setNewShadeInline(e.target.value)} data-testid="inline-shade-input" />
                <Button variant="outline" onClick={addShadeInline} data-testid="add-inline-shade-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label>Make *</Label>
              <Select value={newBatchForm.make_id} onValueChange={(v) => setNewBatchForm({ ...newBatchForm, make_id: v })}>
                <SelectTrigger data-testid="new-batch-make-select"><SelectValue placeholder="Pick make" /></SelectTrigger>
                <SelectContent>
                  {makes.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2 mt-1">
                <Input placeholder="Or add new make" value={newMakeInline} onChange={(e) => setNewMakeInline(e.target.value)} data-testid="inline-make-input" />
                <Button variant="outline" onClick={addMakeInline} data-testid="add-inline-make-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewBatchOpen(false)}>Cancel</Button>
            <Button onClick={saveNewBatch} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-new-batch-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pallet Picker Dialog — operator picks which PRODUCED pallets belong to this DPR */}
      <Dialog open={palletPickerOpen} onOpenChange={setPalletPickerOpen}>
        <DialogContent className="max-w-4xl" data-testid="pallet-picker-dialog">
          <DialogHeader>
            <DialogTitle>Pick pallets for this report</DialogTitle>
            <DialogDescription>
              Showing PRODUCED and PACKED pallets not yet attached to any other report. Tick the ones produced during this shift.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div>
              <Label className="text-xs">Paired from</Label>
              <Input type="date" value={palletFilterFrom} onChange={(e) => setPalletFilterFrom(e.target.value)} data-testid="pallet-filter-from" />
            </div>
            <div>
              <Label className="text-xs">Paired to</Label>
              <Input type="date" value={palletFilterTo} onChange={(e) => setPalletFilterTo(e.target.value)} data-testid="pallet-filter-to" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Search</Label>
              <Input placeholder="pallet ID, product, PO, customer…" value={palletSearch} onChange={(e) => setPalletSearch(e.target.value)} data-testid="pallet-search" />
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Button size="sm" variant="outline" onClick={reloadCandidates} disabled={palletPickerLoading} data-testid="pallet-reload">
              {palletPickerLoading ? "Loading…" : "↻ Refresh"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const next = {};
                filteredCandidates.forEach((p) => { next[p.id] = true; });
                setPickedPalletIds(next);
              }}
              data-testid="pallet-select-all"
            >
              Select all visible ({filteredCandidates.length})
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPickedPalletIds({})} data-testid="pallet-clear-all">
              Clear
            </Button>
            <span className="text-xs text-gray-500 ml-auto" data-testid="pallet-picked-count">
              {Object.values(pickedPalletIds).filter(Boolean).length} selected
            </span>
          </div>

          <div className="border rounded max-h-[420px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="border-b p-2 w-10"></th>
                  <th className="border-b p-2 text-left">Pallet ID</th>
                  <th className="border-b p-2 text-left">State</th>
                  <th className="border-b p-2 text-left">Product</th>
                  <th className="border-b p-2 text-left">PO</th>
                  <th className="border-b p-2 text-left">Customer</th>
                  <th className="border-b p-2 text-right">Qty</th>
                  <th className="border-b p-2 text-left">Paired by</th>
                  <th className="border-b p-2 text-left">Paired at</th>
                </tr>
              </thead>
              <tbody>
                {palletPickerLoading && (
                  <tr><td colSpan={9} className="p-4 text-center text-gray-500">Loading…</td></tr>
                )}
                {!palletPickerLoading && filteredCandidates.length === 0 && (
                  <tr><td colSpan={9} className="p-4 text-center text-gray-500">
                    No unattached pallets in this range.
                  </td></tr>
                )}
                {!palletPickerLoading && filteredCandidates.map((p) => (
                  <tr key={p.id} className="hover:bg-orange-50/40" data-testid={`pallet-row-${p.id}`}>
                    <td className="border-b p-2 text-center">
                      <Checkbox
                        checked={!!pickedPalletIds[p.id]}
                        onCheckedChange={(v) => setPickedPalletIds((prev) => ({ ...prev, [p.id]: !!v }))}
                        data-testid={`pallet-check-${p.id}`}
                      />
                    </td>
                    <td className="border-b p-2 font-mono">{p.id}</td>
                    <td className="border-b p-2">
                      <span
                        className={`text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded ${
                          p.state === "PACKED"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {p.state}
                      </span>
                    </td>
                    <td className="border-b p-2">{p.product_code || "—"}</td>
                    <td className="border-b p-2">{p.po_no || <span className="italic text-gray-400">Unassigned</span>}</td>
                    <td className="border-b p-2">{p.customer_name || "—"}</td>
                    <td className="border-b p-2 text-right">{p.actual_qty ?? p.target_qty ?? "—"}</td>
                    <td className="border-b p-2 text-xs text-gray-600">{p.paired_by_email || "—"}</td>
                    <td className="border-b p-2 text-xs text-gray-600">{p.paired_at ? new Date(p.paired_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPalletPickerOpen(false)} data-testid="pallet-picker-cancel">Cancel</Button>
            <Button onClick={applyPickedPallets} disabled={autoPrefilling} className="bg-orange-600 hover:bg-orange-700" data-testid="pallet-picker-confirm">
              {autoPrefilling ? "Adding…" : `Add ${Object.values(pickedPalletIds).filter(Boolean).length} pallet(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
